<script setup lang="ts">
import { computed, watch, isRef, nextTick, ref, useAttrs, useSlots, type Ref } from "vue";
import { useValidatorField } from "@/composables/ui/inputs/useValidatorField";
import { useFieldValidation } from "@/composables/ui/inputs/useFieldValidation";
import { useSelectOptionItems } from "@/composables/ui/inputs/useSelectOptionItems";
import { useMultiselectBridge } from "@/composables/ui/inputs/useMultiselectBridge";
import {
  useChipFit,
  CHIP_INDEX_ATTR,
  CHIP_OVERFLOW_ATTR,
} from "@/composables/ui/inputs/useChipFit";
import { useI18n } from "vue-i18n";
import { formatForElementId, toCamelCase } from "@/utils/Formatters";
import { useAbility } from "@casl/vue";
import SearchableMultiselect from "@/components/ui/inputs/SearchableMultiselect.vue";
import type { TSelectFieldProps } from "@/types/selectField";
import { useDisplay } from "vuetify";
import { cleanErrorMessage } from "@/utils/FormUtils.ts";
import BaseDialog from "@/components/shared/dialogs/BaseDialog.vue";
import BaseDialogActions from "@/components/shared/dialogs/BaseDialogActions.vue";
import { Tippy } from "vue-tippy";

type ArraySliceConfig = {
  /** Property on each array entry holding the selected item value, e.g. "practice". */
  valueField: string;
  /**
   * Constant fields written into every entry this select owns AND used to identify them,
   * e.g. { type: ECooperatePartiesType.GP }. The select reads/writes only the slice of the
   * array whose entries match these constants, leaving entries owned by other selects intact.
   */
  constants: Record<string, unknown>;
};

type SelectFieldValidatorProps = TSelectFieldProps & {
  hint?: string;
  closeOnSelect?: boolean;
  /**
   * Bind this select to a slice of an array field instead of a scalar/array of scalars.
   * Each picked item is stored as `{ ...constants, [valueField]: <itemValue> }`. Use with a
   * constant `fieldName` shared across several selects, each owning a different `constants` slice.
   */
  arraySlice?: ArraySliceConfig;
};

const props = withDefaults(defineProps<SelectFieldValidatorProps>(), {
  variant: "outlined",
  density: "comfortable",
  hideDetails: false,
  disabled: false,
  readonly: false,
  required: false,
  transparent: false,
  multiple: false,
  chips: false,
  clearable: false,
  loading: false,
  hideNoData: false,
  waitingForBackend: false,
  showValidationIcon: true,
  touched: false,
  itemTitle: "title",
  itemValue: "value",
  returnObject: false,
  autocomplete: "off",
  autoSelectSingleItem: true,
  filterField: "search",
  closeOnSelect: false,
  visibleChips: "fit",
  menuProps: () => ({}),
});

const emit = defineEmits<{
  (e: "focus"): void;
  (e: "blur"): void;
  (e: "update:search", val: string): void;
  (e: "update:modelValue", val: any): void;
}>();

const { smAndDown } = useDisplay();
const { t } = useI18n();
const { can } = useAbility();
const attrs = useAttrs();

const isFieldHidden = computed(() => {
  return props.subject && props.fieldName && !can($permActions.READ, props.subject);
});

const permissionDisabled = computed(() => {
  return Boolean(
    props.disabled ||
    (props.subject &&
      props.fieldName &&
      !can($permActions.CREATE, props.subject) &&
      !can($permActions.UPDATE, props.subject))
  );
});

const {
  value: fieldValue,
  errorMessage: schemaErrorMessage,
  meta,
  baseFieldProps,
  setTouched,
} = useValidatorField(props);

function matchesConstants(entry: unknown): boolean {
  if (!entry || typeof entry !== "object") return false;
  const e = entry as Record<string, unknown>;
  return Object.entries(props.arraySlice!.constants).every(([k, v]) => e[k] === v);
}

// Normalize the autocomplete's emitted value into a flat list of picked item values.
function toSelectedArray(val: unknown): unknown[] {
  if (props.multiple) return Array.isArray(val) ? val : [];
  return val == null ? [] : [val];
}

// In array-slice mode, present the autocomplete with the picked value(s) of this select's slice,
// while the underlying vee-validate field stays the full `{ ...constants, [valueField] }[]` array.
// Without arraySlice it's a pure pass-through of the raw field value.
const displayValue = computed<any>({
  get() {
    if (!props.arraySlice) {
      const v = fieldValue.value;
      // Vuetify 4: only `undefined` means "no selection" — null/"" create phantom selections.
      if (!props.multiple && (v === null || v === "")) return undefined;
      return v;
    }
    const arr = Array.isArray(fieldValue.value) ? fieldValue.value : [];
    const picked = arr
      .filter((e) => matchesConstants(e))
      .map((e) => (e as Record<string, unknown>)[props.arraySlice!.valueField]);
    return props.multiple ? picked : (picked[0] ?? null);
  },
  set(newVal) {
    if (!props.arraySlice) {
      fieldValue.value = newVal;
      return;
    }
    const { valueField, constants } = props.arraySlice;
    const arr = Array.isArray(fieldValue.value) ? fieldValue.value : [];
    const others = arr.filter((e) => !matchesConstants(e));
    const mine = toSelectedArray(newVal).map((v) => ({ ...constants, [valueField]: v }));
    fieldValue.value = [...others, ...mine];
  },
});

const externalErrorMessage = computed(() => {
  const messages = attrs.errorMessages;
  if (!Array.isArray(messages) || messages.length === 0) return undefined;
  const first = messages[0];
  return typeof first === "string" ? first : undefined;
});

const displayErrorMessage = computed(() => schemaErrorMessage.value || externalErrorMessage.value);

// In array-slice mode each select shares one vee-validate field; validate this slice only.
const validationValue = computed(() => (props.arraySlice ? displayValue.value : fieldValue.value));

const { icon: validationIcon, color: validationColor } = useFieldValidation({
  value: validationValue,
  errorMessage: displayErrorMessage,
  metaTouched: meta.touched,
  touched: computed(() => Boolean(props.touched) || Boolean(externalErrorMessage.value)),
  required: computed(() => props.required && !permissionDisabled.value),
});

watch(
  () => props.modelValue,
  (newVal) => {
    if (newVal !== undefined && newVal !== displayValue.value) {
      displayValue.value = newVal;
    }
  },
  { immediate: true }
);

watch(displayValue, (newVal) => {
  emit("update:modelValue", newVal);
});

const menuOpen = ref(false);
const searchValue = ref("");
const ignoreNextSearchEcho = ref(false);
const ignoredSearchText = ref("");

function getItemTitleForValue(val: any): string {
  const selected = optionItems.value.find((item) => {
    const id = props.returnObject ? itemProps.value(item)?.value : itemProps.value(item)?.value;
    return id === val;
  });
  if (!selected) return "";
  return String(itemProps.value(selected)?.title ?? "");
}

function onUpdateSearch(val: string) {
  if (
    ignoreNextSearchEcho.value &&
    (val === "" || val === ignoredSearchText.value || val.trim() === ignoredSearchText.value.trim())
  ) {
    ignoreNextSearchEcho.value = false;
    searchValue.value = "";
    menuOpen.value = false;
    return;
  }
  ignoreNextSearchEcho.value = false;
  searchValue.value = val;
  onSearchUpdate(val);
}

const {
  itemProps,
  optionItems,
  cachedSelectedItems,
  isSearching,
  effectiveNoFilters,
  filterKeys,
  customFilter,
  handleInteraction,
  onSearchUpdate,
  onIntersect,
} = useSelectOptionItems(props, displayValue, (val) => emit("update:search", val));

const interactionBlocked = computed(() => permissionDisabled.value || !!props.readonly);

function openMobileDialog() {
  handleInteraction();

  if (props.queryEnabled && isRef(props.queryEnabled)) {
    (props.queryEnabled as Ref<boolean>).value = true;
  }

  if (
    props.paginatedQuery &&
    !props.paginatedQuery.data?.value?.pages?.length &&
    !props.paginatedQuery.isFetching.value
  ) {
    props.paginatedQuery.refetch();
  }
  if (props.queryAll && !props.queryAll.data?.value && !props.queryAll.isFetching.value) {
    props.queryAll.refetch();
  }
}

const multiselect = useMultiselectBridge(
  displayValue,
  optionItems,
  itemProps,
  () => props.multiple,
  () => props.returnObject,
  interactionBlocked,
  openMobileDialog
);

const useSearchableMultiselect = computed(() => props.multiple || smAndDown.value);

const selectedCount = computed(() =>
  props.multiple ? toSelectedArray(displayValue.value).length : 0
);

const chipFieldRef = ref<HTMLElement | null>(null);

const { visibleChipCount, measuring: measuringChips } = useChipFit({
  container: chipFieldRef,
  visibleChips: computed(() => props.visibleChips),
  selectedCount,
  enabled: computed(() => props.multiple && useSearchableMultiselect.value),
});

const chipLimit = computed(() =>
  measuringChips.value ? Number.POSITIVE_INFINITY : visibleChipCount.value
);

function titleForSelection(val: unknown): string {
  if (props.returnObject && val && typeof val === "object") {
    return String(itemProps.value(val)?.title ?? "");
  }
  return getItemTitleForValue(val);
}

const hiddenChipTitles = computed(() => {
  if (!props.multiple || measuringChips.value) return [];
  return toSelectedArray(displayValue.value)
    .slice(visibleChipCount.value)
    .map((val) => titleForSelection(val))
    .filter(Boolean);
});

const slots = useSlots();
const nonItemSlots = computed(() => {
  const { item: _, selection: _selection, ...rest } = slots;
  return rest;
});

const htmlInputName = computed(() => {
  if (!props.arraySlice) return props.fieldName;
  const sliceKey = Object.entries(props.arraySlice.constants)
    .map(([key, value]) => `${key}_${value}`)
    .join("_");
  return `${props.fieldName}_${sliceKey}`;
});

const selectFieldProps = computed(() => ({
  ...baseFieldProps.value,
  items: optionItems.value,
  multiple: props.multiple,
  label: props.label ?? t(`generic.fields.${toCamelCase(props.fieldName)}`),
  placeholder: props.placeholder || t("generic.action.select"),
  chips: props.chips,
  clearable: props.clearable,
  loading:
    props.loading ||
    props.paginatedQuery?.isFetching.value ||
    props.queryAll?.isFetching.value ||
    !!props.itemQuery?.isFetching?.value,
  hideNoData:
    props.hideNoData ||
    optionItems.value.length > 0 ||
    props.paginatedQuery?.isFetching.value ||
    props.queryAll?.isFetching.value,
  itemProps: itemProps.value,
  returnObject: props.returnObject,
  error: !!displayErrorMessage.value,
  errorMessages: cleanErrorMessage(displayErrorMessage.value ?? "", t),
  color: validationColor.value,
  active: Array.isArray(displayValue.value) ? displayValue.value.length === 0 : !displayValue.value,
  autocomplete: props.autocomplete,
  customFilter: customFilter,
  filterKeys: filterKeys.value,
  noFilter: effectiveNoFilters.value,
  prependInnerIcon: props.prependInnerIcon,
  closableChips: props.closableChips || (props.multiple && props.chips),
  noDataText: props.noDataText,
  hint: props.hint,
  persistentHint: !!props.hint,
  name: htmlInputName.value,
}));

const elementId = computed(() => formatForElementId(selectFieldProps.value.label));

const mobileMenuContentClass =
  "tw:!fixed tw:!left-4 tw:!right-4 tw:!top-1/2 tw:!-translate-y-1/2 tw:!max-w-[calc(100vw-2rem)] tw:!w-[calc(100vw-2rem)] tw:!max-h-[80vh] tw:!rounded-lg tw:z-app-overlay";

const menuProps = computed(() => {
  const customContentClass =
    typeof props.menuProps?.contentClass === "string" ? props.menuProps.contentClass : "";
  const desktopContentClass = ["select-field-validator__menu", customContentClass]
    .filter(Boolean)
    .join(" ");

  return {
    scrollStrategy: "none" as const,
    maxWidth: 0,
    offset: 4,
    disabled: smAndDown.value,
    "data-testid": "select-menu",
    "data-test-elementid": `${elementId.value}-menu`,
    ...props.menuProps,
    contentClass: smAndDown.value ? mobileMenuContentClass : desktopContentClass,
    ...(smAndDown.value ? { disabled: true } : {}),
  };
});

function onFocus() {
  handleInteraction();
  emit("focus");
}

function onBlur() {
  emit("blur");
}

function resetSearchAfterSelect(selectedVal: any) {
  if (!props.closeOnSelect || props.multiple || selectedVal == null) return;
  ignoreNextSearchEcho.value = true;
  ignoredSearchText.value = getItemTitleForValue(selectedVal);
  nextTick(() => {
    searchValue.value = "";
    onSearchUpdate("");
    menuOpen.value = false;
  });
}

function onMobileConfirm() {
  const selectedId = multiselect.pendingIds.value[0];
  multiselect.onConfirm();
  if (selectedId != null) {
    resetSearchAfterSelect(selectedId);
  }
}

function onUpdateModelValue(newVal: any) {
  const isIdEqual = (val1: any, val2: any) => {
    const id1 = props.returnObject ? itemProps.value(val1)?.value : val1;
    const id2 = props.returnObject ? itemProps.value(val2)?.value : val2;
    return id1 === id2;
  };

  const getDisabledState = (valItem: any) => {
    const id = props.returnObject ? itemProps.value(valItem)?.value : valItem;
    let item = cachedSelectedItems.value.find((it) => itemProps.value(it)?.value === id);
    if (!item) {
      item = optionItems.value.find((it) => itemProps.value(it)?.value === id);
    }
    return item ? itemProps.value(item)?.disabled : false;
  };

  if (props.multiple) {
    const oldValArr = Array.isArray(displayValue.value)
      ? displayValue.value
      : displayValue.value == null
        ? []
        : [displayValue.value];
    const newValArr = Array.isArray(newVal) ? [...newVal] : newVal != null ? [newVal] : [];

    let keptAny = false;
    for (const oldVal of oldValArr) {
      if (!newValArr.some((nv) => isIdEqual(nv, oldVal)) && getDisabledState(oldVal)) {
        newValArr.push(oldVal);
        keptAny = true;
      }
    }

    if (keptAny) {
      displayValue.value = newValArr;
      return;
    }
  } else {
    const oldVal = displayValue.value;
    if (
      oldVal != null &&
      (newVal == null || !isIdEqual(newVal, oldVal)) &&
      getDisabledState(oldVal)
    ) {
      const temp = oldVal;
      displayValue.value = newVal;
      nextTick(() => {
        displayValue.value = temp;
      });
      return;
    }
  }

  setTouched(true);
  displayValue.value = newVal;
  resetSearchAfterSelect(newVal);
}
</script>

<template>
  <div
    v-if="!isFieldHidden && useSearchableMultiselect"
    :class="[
      baseFieldProps.class,
      {
        'v-input--required': props.required,
        'v-input--waiting-for-backend': props.waitingForBackend,
        'select-field-validator--chips': props.multiple,
      },
      props.class,
    ]"
    data-testid="select-field-validator"
    :data-test-elementid="elementId"
  >
    <div
      ref="chipFieldRef"
      class="tw:relative"
    >
      <v-autocomplete
        :model-value="displayValue"
        v-bind="{ ...selectFieldProps, ...$attrs }"
        :chips="props.multiple"
        :closable-chips="false"
        :class="[
          props.class,
          {
            'v-input--required': props.required,
            'v-input--waiting-for-backend': props.waitingForBackend,
          },
        ]"
        :disabled="permissionDisabled"
        :clearable="false"
        menu-icon="mdi-chevron-down"
        tabindex="-1"
        :data-test-elementid="`${elementId}-trigger`"
      >
        <template
          v-if="props.showValidationIcon && !props.readonly"
          #append-inner
        >
          <v-icon
            :icon="validationIcon"
            :color="validationColor"
            class="tw:transition-all tw:duration-300 tw:ease-out tw:opacity-100"
          />
        </template>
        <template
          v-if="props.multiple"
          #chip="{ internalItem, props: chipProps, index }"
        >
          <v-chip
            v-if="index < chipLimit"
            v-bind="chipProps"
            :[CHIP_INDEX_ATTR]="index"
            size="small"
            variant="tonal"
            color="secondary"
            class="tw:shrink-0"
          >
            {{ internalItem.props?.title || internalItem.title || internalItem.raw?.name }}
          </v-chip>
          <v-chip
            v-else-if="index === chipLimit"
            :[CHIP_OVERFLOW_ATTR]="true"
            size="small"
            variant="tonal"
            color="secondary"
            class="tw:shrink-0 tw:max-w-24 tw:truncate"
          >
            {{
              t("generic.fields.multiselect.hiddenCount", {
                count: selectedCount - visibleChipCount,
              })
            }}
          </v-chip>
        </template>
        <template #selection="scope">
          <slot
            v-if="$slots.selection && (!isSearching || props.multiple)"
            name="selection"
            v-bind="scope"
          />
          <div
            v-else-if="!isSearching || props.multiple"
            data-testid="selected-value"
            :class="`${props.transparent && 'tw:text-lg tw:!pb-0'} tw:truncate tw:min-w-0`"
          >
            {{
              scope.internalItem.props?.title ||
              scope.internalItem.title ||
              scope.internalItem.raw.name
            }}
            <div
              v-if="scope.internalItem.props?.subtitle"
              class="tw:text-xxs tw:font-medium tw:text-primary/50 tw:leading-none"
            >
              {{ scope.internalItem.props?.subtitle }}
            </div>
          </div>
        </template>
      </v-autocomplete>
      <div
        class="tw:absolute tw:inset-0"
        :class="permissionDisabled || props.readonly ? 'tw:cursor-default' : 'tw:cursor-pointer'"
        data-testid="multi-activator"
        @click="!permissionDisabled && !props.readonly && multiselect.openDialog()"
      >
        <tippy
          v-if="hiddenChipTitles.length > 0"
          tag="div"
          class="tw:absolute tw:inset-0"
          :max-width="450"
          theme="orbis"
        >
          <template #content>
            <div
              class="tw:flex tw:flex-col tw:gap-1 tw:!p-1"
              data-testid="hidden-chips-tooltip"
            >
              <div
                v-for="(title, hiddenIndex) in hiddenChipTitles"
                :key="hiddenIndex"
                class="tw:text-xs"
              >
                {{ title }}
              </div>
            </div>
          </template>
        </tippy>
      </div>
    </div>
  </div>
  <v-autocomplete
    v-else-if="!isFieldHidden"
    v-model:menu="menuOpen"
    v-model:search="searchValue"
    :model-value="displayValue"
    v-bind="{ ...selectFieldProps, ...$attrs }"
    :class="[
      props.class,
      {
        'v-input--required': props.required,
        'v-input--waiting-for-backend': props.waitingForBackend,
      },
    ]"
    :disabled="permissionDisabled"
    @update:model-value="onUpdateModelValue"
    :menu-props="menuProps"
    :readonly="props.readonly || smAndDown"
    @focus="onFocus"
    @blur="onBlur"
    @click:control="openMobileDialog"
    data-testid="select-field-validator"
    :menu-icon="smAndDown ? '' : 'mdi-chevron-down'"
    :data-test-elementid="elementId"
    @update:search="onUpdateSearch"
  >
    <template
      v-if="props.showValidationIcon && !props.readonly"
      #append-inner
    >
      <v-icon
        :icon="validationIcon"
        :color="validationColor"
        class="tw:transition-all tw:duration-300 tw:ease-out tw:opacity-100"
      />
    </template>

    <template #selection="scope">
      <slot
        v-if="$slots.selection && (!isSearching || props.multiple)"
        name="selection"
        v-bind="scope"
      />
      <div
        v-else-if="!isSearching || props.multiple"
        data-testid="selected-value"
        :class="`${props.transparent && 'tw:text-lg tw:!pb-0'} tw:truncate tw:min-w-0`"
      >
        {{ scope.internalItem.props?.title || scope.internalItem.title }}
        <div
          v-if="scope.internalItem.props?.subtitle"
          class="tw:text-xxs tw:font-medium tw:text-primary/50 tw:leading-none"
        >
          {{ scope.internalItem.props?.subtitle }}
        </div>
      </div>
    </template>

    <template #item="scope">
      <slot
        v-if="$slots.item"
        name="item"
        v-bind="scope"
        :props="{ ...scope.props, 'data-testid': 'select-item', style: 'pointer-events: all' }"
      />
      <v-list-item
        v-else
        v-bind="scope.props"
        data-testid="select-item"
        style="pointer-events: all"
      />
    </template>

    <template #append-item>
      <div
        v-if="props.paginatedQuery"
        v-intersect="onIntersect"
        :class="props.paginatedQuery.hasNextPage.value ? 'tw:text-center tw:!py-2' : ''"
      />
      <div
        v-if="props.paginatedQuery?.isFetching.value"
        class="tw:text-caption tw:text-center tw:!py-2"
      >
        <v-progress-circular
          indeterminate
          size="20"
          color="secondary"
        />
      </div>
    </template>

    <template
      v-for="(_, slot) in nonItemSlots"
      #[slot]="scope"
    >
      <slot
        :name="slot"
        v-bind="scope"
      />
    </template>
  </v-autocomplete>
  <!-- back-guard-ignore: opts this picker out of the overlay-back-guard's spare history entry.
       Confirm closes it in the same tick as the field's own router.replace; the guard's cleanup
       history.back() would otherwise unwind that write (see overlayBackGuard.ts). -->
  <BaseDialog
    v-model="multiselect.dialogOpen.value"
    persistent
    :show-icon="false"
    :dialog-props="{ class: 'back-guard-ignore' }"
  >
    <template #title>
      {{ props.label ?? selectFieldProps.label }}
    </template>
    <template #body>
      <SearchableMultiselect
        v-model="multiselect.pendingIds.value"
        :items="multiselect.items.value"
        :search-placeholder="props.placeholder ?? t('generic.action.select')"
        :multiple="props.multiple"
        :fill-height="smAndDown"
        use-secondary-color
        :is-fetching-next-page="props.paginatedQuery?.isFetchingNextPage.value || false"
        @fetch-next-page="onIntersect"
        :disable-local-filter="!!props.paginatedQuery || effectiveNoFilters"
        @update:search="onSearchUpdate"
      />
    </template>
    <template #actions>
      <BaseDialogActions
        :secondary="{
          action: () => {
            multiselect.closeDialog();
          },
        }"
        :primary="{ action: onMobileConfirm }"
      />
    </template>
  </BaseDialog>
</template>

<style src="@/assets/styles/components/fields.scss" scoped lang="scss" />

<style scoped lang="scss">
.v-field__append-inner > .v-icon {
  opacity: 1 !important;
}

.select-field-validator--chips {
  :deep(.v-field__input) {
    flex-wrap: nowrap;
    overflow: hidden;
  }
}

:global(.select-field-validator__menu.v-overlay__content) {
  overflow: hidden;
  border: 1px solid color-mix(in oklab, rgb(var(--v-theme-on-surface)), transparent 88%);
  border-radius: 12px !important;
  background: rgb(var(--v-theme-surface));
  box-shadow: 0 4px 12px rgba(var(--v-theme-shadow), 0.12) !important;
}

:global(.select-field-validator__menu .v-list) {
  padding: 6px;
  background: rgb(var(--v-theme-surface));
}

:global(.select-field-validator__menu .v-list-item) {
  border-radius: 8px;
}

:global(.select-field-validator__menu .v-list-item:hover > .v-list-item__overlay) {
  opacity: 0.04;
}

:global(.select-field-validator__menu .v-list-item--active > .v-list-item__overlay) {
  background: rgb(var(--v-theme-secondary));
  opacity: 0.08;
}
</style>
